import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateCommissionHistory } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateCommissionHistoryItem } from '../../services/affiliateService'
import {
  formatCommissionHistoryDateTime,
  formatCommissionMemberLabel,
} from '../../utils/affiliateHistoryUtils'
import './affiliateLayout.css'

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateIncomePage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const history = await getAffiliateCommissionHistory(100)
        if (!cancelled) {
          setCommissionHistory(history)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const totalCommission = commissionHistory.reduce(
    (sum, item) => sum + Number(item.commission ?? 0),
    0,
  )

  if (loading) {
    return (
      <div className="htd-aff__loading">
        <div className="spinner-border" role="status" />
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="htd-aff__shell">
        <h3>โหลดข้อมูลไม่สำเร็จ</h3>
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="htd-aff__stack">
      <section className="htd-aff__stats">
        <div className="htd-aff__stat" style={{ gridColumn: '1 / -1' }}>
          <span>คอมมิชชั่นรวมจากประวัติ (บาท)</span>
          <strong className="is-accent">{walletService.formatCurrency(totalCommission)}</strong>
        </div>
      </section>

      {commissionHistory.length > 0 ? (
        <section className="htd-aff__panel">
          <div className="htd-aff__panel-head">
            <div className="d-flex align-items-center gap-2">
              <span className="htd-aff__head-icon htd-aff__head-icon--green" aria-hidden>
                <i className="fas fa-chart-line" />
              </span>
              <div>
                <h3>ประวัติคอมมิชชั่น</h3>
                <p>รายการล่าสุด {commissionHistory.length} รายการ</p>
              </div>
            </div>
          </div>
          <div className="htd-aff__table-wrap">
            <table className="htd-aff__table htd-aff__table--stack">
              <thead>
                <tr>
                  <th>วันที่ เวลา</th>
                  <th>สมาชิก</th>
                  <th>ยอดเสีย/ยอดแทง</th>
                  <th>คอมมิชชั่น</th>
                </tr>
              </thead>
              <tbody>
                {commissionHistory.map((item, i) => (
                  <tr key={item._id || `${item.createdAt || item.date}-${i}`}>
                    <td data-label="วันที่ เวลา">{formatCommissionHistoryDateTime(item)}</td>
                    <td data-label="สมาชิก">{formatCommissionMemberLabel(item, maskMember)}</td>
                    <td data-label="ยอดเสีย/ยอดแทง">{walletService.formatCurrency(item.betAmount ?? 0)}</td>
                    <td className="is-gain" data-label="คอมมิชชั่น">{walletService.formatCurrency(item.commission ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="htd-aff__empty">
          <i className="fas fa-receipt" />
          <h4>ไม่มีประวัติคอมมิชชั่น</h4>
        </div>
      )}
    </div>
  )
}

export default AffiliateIncomePage
